"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function OliveBranchRule({
  className = ""
}: {
  className?: string;
}) {
  return <div className={`flex items-center justify-center w-full ${className}`}>
      <span className="flex-1 h-px bg-border" />
      <span className="mx-2 inline-block h-1 w-1 rounded-lg" style={{
      backgroundColor: "hsl(198 80% 45%)"
    }} aria-hidden="true" />
      <span className="flex-1 h-px bg-border" />
    </div>;
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-1">
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-28 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className="bg-transparent hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent px-0 py-1">
              <Link to={item.href || "#"} className="relative text-foreground font-default text-sm tracking-wider uppercase transition-colors hover:text-[hsl(198_80%_45%)] focus:text-[hsl(198_80%_45%)] group" style={{
            letterSpacing: "0.06em",
            fontWeight: 600
          }}>
                {item.label}
                <span className="absolute left-0 -bottom-2 w-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <OliveBranchRule />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 px-6">
        <div className="h-8 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-2/3 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-3/4 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col px-6 pb-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} onClick={onNavigate} className="block py-5 font-default italic text-3xl text-foreground hover:text-[hsl(198_80%_45%)] transition-colors" style={{
        fontWeight: 500,
        lineHeight: 1.1
      }}>
            {item.label}
          </Link>
          <OliveBranchRule />
        </div>)}
      <div className="mt-8">
        <Button asChild className="w-full bg-[hsl(45_100%_55%)] text-[hsl(215_25%_27%)] hover:bg-[hsl(45_90%_60%)] uppercase tracking-widest font-default font-semibold rounded-lg h-12" style={{
        letterSpacing: "0.08em"
      }}>
          <Link to="/ParentToolboxPage" onClick={onNavigate}>
            Permission Slip
          </Link>
        </Button>
      </div>
    </nav>;
}
export default function ClassroomHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1037" id="classroom-header" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${scrolled ? "bg-background/90 backdrop-blur-md border-b border-border shadow-sm" : "bg-background border-b border-transparent"}`}>
      <div className="mx-auto w-full max-w-[1320px] px-5 md:px-8">
        <div className="grid grid-cols-12 items-center h-16 md:h-20 gap-4">
          <div className="col-span-8 md:col-span-9 flex items-center gap-6">
            <Link to="/" className="flex items-center" aria-label="Class2A home">
              <WvcLogo className="h-8 w-auto" />
            </Link>

            <div className="hidden lg:flex items-center gap-3 pl-2">
              <span className="h-px w-6 bg-border" aria-hidden="true" />
              <span className="font-mono uppercase text-muted-foreground" style={{
              fontSize: "11px",
              letterSpacing: "0.18em",
              fontWeight: 600
            }}>&nbsp;CLASS 2A — LEARNING HUB</span>
            </div>
          </div>

          <div className="col-span-4 md:col-span-3 flex items-center justify-end gap-6">
            <div className="hidden md:flex items-center gap-6">
              <MenuProvider menu_id="2">
                <DesktopMenuConsumer />
              </MenuProvider>

              <Button asChild size="sm" className="bg-[hsl(45_100%_55%)] text-[hsl(215_25%_27%)] hover:bg-[hsl(45_90%_60%)] uppercase font-default font-semibold rounded-lg h-9 px-5 group" style={{
              letterSpacing: "0.08em",
              fontSize: "12px"
            }}>
                <Link to="/ParentToolboxPage" className="flex items-center gap-1.5">
                  <span>Forms</span>
                  <span className="font-default italic opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" aria-hidden="true">
                    →
                  </span>
                </Link>
              </Button>
            </div>

            <div className="md:hidden flex items-center">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Open menu" className="text-foreground hover:bg-transparent hover:text-[hsl(198_80%_45%)]">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-background w-[88%] sm:max-w-sm p-0 border-l border-border">
                  <SheetHeader className="px-6 pt-6 pb-2">
                    <SheetTitle asChild>
                      <div className="flex items-center justify-between">
                        <WvcLogo className="h-7 w-auto" />
                      </div>
                    </SheetTitle>
                  </SheetHeader>

                  <div className="px-6">
                    
                  </div>

                  <Separator className="my-4 bg-border" />

                  <MenuProvider menu_id="2">
                    <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                  </MenuProvider>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </section>;
}